<script setup lang="ts">
import type { IconName } from "../Icon/icons";
import { useResizeObserver } from "@vueuse/core";
import { computed, onBeforeUnmount, onMounted, ref, toRefs, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";
import { tryUseDialogLayer } from "../../composables/Dialog/useDialogLayer";
import VIcon from "../Icon/VIcon.vue";
import VSteps from "../Steps/VSteps.vue";
import { useDialogStepperState } from "./useDialogStepperState";

interface Props {
  stepIndex: number
  previousButtonType?: "default" | "disabled" | "none"
  nextButtonType?: "default" | "disabled" | "none" | "submit"
  description?: string
  icon?: IconName
  /**
   * Lays the scroll area out as a flex column so a step body can size itself against the available
   * space (`flex-1`) or center in it (`my-auto`) without depending on percentage heights, which only
   * resolve when every ancestor has an explicit height. Opt-in: block layout stays the default.
   */
  flexContent?: boolean
  /**
   * Padding of the footer in a paddingless dialog: `default` is 20px all round, `wide` the 28px sides and 76px height
   * of the App Store wizard boards.
   */
  footerPadding?: "default" | "wide"
}

const props = withDefaults(defineProps<Props>(), {
  previousButtonType: "default",
  nextButtonType: "default",
  description: undefined,
  icon: undefined,
  flexContent: false,
  footerPadding: "default",
});

const emit = defineEmits<{
  (e: "submit"): void
}>();

const { t } = useI18n();
const {
  stepIndex,
  previousButtonType,
  nextButtonType,
  description,
  icon,
  flexContent,
  footerPadding,
} = toRefs(props);

const {
  currentStep,
  totalSteps,
  stepLabels,
  railInteractive,
  railVariant,
  goToStep,
  registerStep,
  unregisterStep,
  nextStep,
  previousStep,
} = useDialogStepperState();

onMounted(() => {
  registerStep({
    stepIndex: stepIndex.value,
    previousButtonType: previousButtonType.value,
    nextButtonType: nextButtonType.value,
    description: description.value,
    icon: icon.value,
  });
});

onBeforeUnmount(() => {
  unregisterStep(stepIndex.value);
});

const railSteps = computed(() => stepLabels.value ?? totalSteps.value);
const stepsVariant = computed(() => {
  if (railVariant.value === "current") {
    return "current";
  }
  return stepLabels.value ? "labeled" : "compact";
});

const isActive = computed(() => stepIndex.value === currentStep.value);
const layer = tryUseDialogLayer();
const paddingless = computed(() => Boolean(layer?.paddingless));
const footerPaddingClass = computed(() => {
  if (!paddingless.value) {
    return "mt-5 pt-5";
  }
  return footerPadding.value === "wide" ? "dialog-stepper-footer--wide px-7 py-[22px]" : "p-5";
});
// The rail sits in the middle of the footer whatever the actions beside it are. Both side columns get the
// width of the wider action group, so the middle column, and the rail centred in it, line up with the
// footer's centre. When the actions leave the rail too little room, the rail moves above them.
const RAIL_MIN_WIDTH = 96;
const COLUMN_GAP = 12;
const footerEl = useTemplateRef<HTMLElement>("footer");
const previousEl = useTemplateRef<HTMLElement>("previousActions");
const nextEl = useTemplateRef<HTMLElement>("nextActions");
const footerWidth = ref(0);
const previousWidth = ref(0);
const nextWidth = ref(0);

function borderBoxWidth(entry: ResizeObserverEntry): number {
  return entry.borderBoxSize?.[0]?.inlineSize ?? entry.contentRect.width;
}

useResizeObserver(footerEl, ([entry]) => {
  footerWidth.value = entry.contentRect.width;
});
useResizeObserver(previousEl, ([entry]) => {
  previousWidth.value = borderBoxWidth(entry);
});
useResizeObserver(nextEl, ([entry]) => {
  nextWidth.value = borderBoxWidth(entry);
});

const sideWidth = computed(() => Math.ceil(Math.max(previousWidth.value, nextWidth.value)));
const stacked = computed(() =>
  footerWidth.value > 0 && footerWidth.value < 2 * (sideWidth.value + COLUMN_GAP) + RAIL_MIN_WIDTH,
);
const footerLayoutClass = computed(() =>
  stacked.value
    ? "dialog-stepper-footer--stacked flex flex-wrap items-center justify-between gap-3"
    : "grid grid-cols-[var(--stepper-side,max-content)_minmax(0,1fr)_var(--stepper-side,max-content)] gap-x-3",
);
const footerStyle = computed(() =>
  !stacked.value && sideWidth.value > 0 ? { "--stepper-side": `${sideWidth.value}px` } : undefined,
);

// Announced by the progressbar and the polite region below; it is not drawn, the rail already shows the position.
const stepCounterLabel = computed(() =>
  t("base.step_counter", { current: currentStep.value + 1, total: totalSteps.value }),
);
</script>

<template>
  <div v-show="isActive" class="flex flex-col flex-1 min-h-0">
    <div v-if="icon || description" class="flex flex-col items-center shrink-0 mb-4">
      <VIcon
        v-if="icon"
        class="text-primary-500 text-3xl mb-4"
        :name="icon"
        aria-hidden="true"
      />
      <div v-if="description" class="text-center max-w-xs font-medium text-(--text)">
        {{ description }}
      </div>
    </div>

    <!-- In a paddingless dialog the footer follows directly, so the scrolled content keeps its own room at the bottom. -->
    <div
      class="dialog-stepper-scroll flex-1 min-h-0 overflow-y-auto overflow-x-hidden"
      :class="[paddingless ? 'pb-5' : 'pb-4', { 'flex flex-col': flexContent }]"
    >
      <slot name="content" />
    </div>

    <div
      ref="footer"
      class="dialog-stepper-footer bg-(--background-modal) border-t border-(--input-border) items-center z-10 shrink-0"
      :class="[footerLayoutClass, footerPaddingClass]"
      :style="footerStyle"
    >
      <!-- Custom previous button OR default button. Sized to its content so the footer can measure it. -->
      <div ref="previousActions" class="w-max justify-self-start">
        <slot name="previous">
          <VButton
            v-if="previousButtonType !== 'none'"
            rounded
            variant="gray"
            :disabled="currentStep < 1 || previousButtonType === 'disabled'"
            @click="previousStep"
          >
            {{ t(`base.previous`) }}
          </VButton>
          <div v-else />
        </slot>
      </div>

      <div
        class="dialog-stepper-rail flex flex-col items-stretch min-w-0"
        :class="{ 'order-first basis-full': stacked }"
      >
        <VSteps
          :model-value="currentStep"
          :steps="railSteps"
          :variant="stepsVariant"
          :interactive="railInteractive"
          :value-text="stepCounterLabel"
          @update:model-value="goToStep"
        />
        <!-- The rail alone gives a screen reader nothing to announce on navigation: a progressbar's
             value change is not reliably spoken, so the position is mirrored into a polite region. -->
        <span class="sr-only" role="status">{{ stepCounterLabel }}</span>
      </div>

      <div ref="nextActions" class="w-max justify-self-end">
        <slot name="next">
          <VButton
            v-if="nextButtonType === 'submit'"
            rounded
            @click="emit('submit')"
          >
            {{ t(`base.submit`) }}
          </VButton>
          <VButton
            v-else-if="nextButtonType !== 'none'"
            rounded
            :disabled="currentStep >= totalSteps - 1 || nextButtonType === 'disabled'"
            @click="nextStep"
          >
            {{ t(`base.next`) }}
          </VButton>
          <div v-else />
        </slot>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* The scroll area spans the full dialog width and takes the dialog's side padding inside, so its
   scrollbar sits on the dialog's edge instead of 20px in, while the content keeps its place. */
.dialog-stepper-scroll {
  margin-inline: calc(-1 * var(--dialog-padding, 20px));
  padding-inline: var(--dialog-padding, 20px);
}

.dialog-stepper-footer {
  margin-inline: calc(-1 * var(--dialog-padding, 20px));
  margin-bottom: calc(-1 * var(--dialog-padding, 20px));
}

.dialog-stepper-footer:not(.p-5, .dialog-stepper-footer--wide) {
  padding: 16px 20px var(--dialog-padding, 20px);
}
</style>
